const AWIN_ADVERTISER_ID = "127831";

const STORAGE_KEYS = {
  awc: "sb_awin_awc",
  channel: "sb_awin_channel",
};

// Keys required by the Awin Shopify app for headless S2S (note_attributes).
const CART_ATTRIBUTE_KEYS = {
  awc: "__awc",
  channel: "__awin_channel",
};

const LEGACY_ATTRIBUTE_KEYS = ["awc", "_awin_awc", "awin_awc"];

function getCookieDomain() {
  if (typeof window === "undefined") return null;

  const { hostname } = window.location;
  if (hostname === "localhost" || hostname.endsWith(".local")) return null;

  const parts = hostname.split(".");
  if (parts.length >= 2) {
    return `.${parts.slice(-2).join(".")}`;
  }

  return null;
}

function setAwinCookie(name, value) {
  if (typeof document === "undefined" || !value) return;

  const maxAge = 60 * 60 * 24 * 365;
  const domain = getCookieDomain();
  const domainPart = domain ? `; domain=${domain}` : "";
  const secure = window.location.protocol === "https:" ? "; Secure" : "";

  document.cookie = `${name}=${encodeURIComponent(
    value
  )}; path=/${domainPart}; max-age=${maxAge}${secure}; SameSite=Lax`;
}

export function captureAwcFromUrl() {
  if (typeof window === "undefined") return null;

  const params = new URLSearchParams(window.location.search);
  const awc = params.get("awc") || params.get("AWC");
  const source = params.get("source");

  if (source) {
    localStorage.setItem(STORAGE_KEYS.channel, source);
    setAwinCookie("_aw_channel", source);
  }

  if (awc) {
    localStorage.setItem(STORAGE_KEYS.awc, awc);
    setAwinCookie(`_awc_${AWIN_ADVERTISER_ID}`, awc);

    if (!source && !getStoredChannel()) {
      localStorage.setItem(STORAGE_KEYS.channel, "aw");
      setAwinCookie("_aw_channel", "aw");
    }

    return awc;
  }

  return getStoredAwc();
}

export function getStoredAwc() {
  if (typeof window === "undefined") return null;
  return localStorage.getItem(STORAGE_KEYS.awc);
}

function getStoredChannel() {
  if (typeof window === "undefined") return null;
  return localStorage.getItem(STORAGE_KEYS.channel);
}

export function getCheckoutUrl(checkout) {
  if (!checkout?.webUrl) return null;

  return checkout.webUrl.replace(
    "shannon-bond-us.myshopify.com/cart/c/",
    "shannon-bond-us.myshopify.com/checkouts/cn/"
  );
}

function buildAwinAttributes(existingAttributes, awc, channel) {
  const attributes = (existingAttributes || []).filter(
    (attr) => !LEGACY_ATTRIBUTE_KEYS.includes(attr.key)
  );

  const upsert = (key, value) => {
    const index = attributes.findIndex((attr) => attr.key === key);
    if (index >= 0) {
      attributes[index] = { key, value };
    } else {
      attributes.push({ key, value });
    }
  };

  upsert(CART_ATTRIBUTE_KEYS.awc, awc);
  if (channel) {
    upsert(CART_ATTRIBUTE_KEYS.channel, channel);
  }

  return attributes;
}

function hasSyncedAwin(existingAttributes, awc, channel) {
  const awcSynced =
    existingAttributes?.find((attr) => attr.key === CART_ATTRIBUTE_KEYS.awc)
      ?.value === awc;

  const channelSynced =
    !channel ||
    existingAttributes?.find(
      (attr) => attr.key === CART_ATTRIBUTE_KEYS.channel
    )?.value === channel;

  return awcSynced && channelSynced;
}

export async function syncAwcToCheckout(client, checkout) {
  const awc = getStoredAwc();
  if (!awc || !checkout?.id) return checkout;

  const channel = getStoredChannel() || "aw";
  const existing = checkout.customAttributes || [];

  if (hasSyncedAwin(existing, awc, channel)) return checkout;

  try {
    const updatedCheckout = await client.checkout.updateAttributes(checkout.id, {
      customAttributes: buildAwinAttributes(existing, awc, channel),
    });

    if (updatedCheckout.userErrors?.length) {
      console.error(
        "Awin attribute sync returned userErrors:",
        updatedCheckout.userErrors
      );
      return checkout;
    }

    return updatedCheckout;
  } catch (error) {
    console.error("Failed to sync Awin attributes to checkout:", error);
    return checkout;
  }
}

export function getAwcCreateInput() {
  const awc = getStoredAwc();
  if (!awc) return {};

  const channel = getStoredChannel() || "aw";

  return {
    customAttributes: [
      { key: CART_ATTRIBUTE_KEYS.awc, value: awc },
      { key: CART_ATTRIBUTE_KEYS.channel, value: channel },
    ],
  };
}
